import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button, Input } from '../components/core';
import { updateOrg } from '../services/org.service';
import { COUNTRIES } from '../constants.ts';

export default function OrgSettingsPage() {
  const { org, orgId, orgSlug } = useOrg();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [name, setName] = useState(org.name);
  const [logoUrl, setLogoUrl] = useState(org.logoUrl ?? '');
  const [country, setCountry] = useState(org.country);
  const [contactName, setContactName] = useState(org.contactName);
  const [contactPhone, setContactPhone] = useState(org.contactPhone);
  const [contactEmail, setContactEmail] = useState(org.contactEmail);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasChanges =
    name !== org.name ||
    logoUrl !== (org.logoUrl ?? '') ||
    country !== org.country ||
    contactName !== org.contactName ||
    contactPhone !== org.contactPhone ||
    contactEmail !== org.contactEmail;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!hasChanges) return;

    setIsSubmitting(true);

    try {
      await updateOrg(orgId, {
        name: name.trim(),
        logoUrl: logoUrl.trim() || undefined,
        country,
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim(),
      });
      toast.success(t('orgSettings.successMessage'));
    } catch (error) {
      console.error('Failed to update organization:', error);
      toast.error(t('orgSettings.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar
        displayName={user?.displayName ?? null}
        onSignOut={signOut}
        onLogoClick={() => navigate('/')}
        onOrgNameClick={() => navigate(`/org/${orgSlug}`)}
        onAvatarClick={() => navigate("/profile")}
        orgName={org.name}
      />

      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <h1 className="mb-1 font-display text-2xl font-semibold text-gray-900">
          {t('orgSettings.title')}
        </h1>
        <p className="mb-8 font-body text-sm text-gray-500">
          {t('orgSettings.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Organization Info */}
          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="font-body text-sm font-semibold text-gray-700">{t('orgSettings.organizationInfo')}</h2>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t('orgSettings.organizationName')}
                  required
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Khalil Farms"
                  autoFocus
                />
                <div className="flex flex-col gap-1.5">
                  <label className="text-3xs font-semibold tracking-wider text-gray-700">
                    URL Slug <span className="ms-0.5 text-gray-400">{t('orgSettings.cannotBeChanged')}</span>
                  </label>
                  <div className="flex items-center gap-2 rounded-sm border border-gray-300 bg-gray-50 px-3.5 py-2.75 font-mono text-sm text-gray-600">
                    <span>{org.slug}</span>
                    <span className="ms-auto text-xs text-gray-400">🔒 {t('orgSettings.lockedAfterCreation')}</span>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-3xs font-semibold tracking-wider text-gray-700">
                    {t('orgSettings.country')} <span className="ms-0.5 text-red-600">*</span>
                  </label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                    className="w-full rounded-sm border border-gray-300 bg-white px-3.5 py-2.75 font-body text-base text-gray-900 outline-none transition focus:border-green-600 focus:ring-4 focus:ring-green-600/15"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label={t('orgSettings.logoUrl')}
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://…"
                  helperText={t('orgSettings.optional')}
                />
              </div>
            </div>
          </section>

          {/* Contact Details */}
          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h2 className="font-body text-sm font-semibold text-gray-700">{t('orgSettings.contactDetails')}</h2>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5">
              <Input
                label={t('orgSettings.contactName')}
                required
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="e.g. Ahmad Khalil"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t('orgSettings.contactPhone')}
                  required
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="e.g. 050-123-4567"
                />
                <Input
                  label={t('orgSettings.contactEmail')}
                  required
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="e.g. ahmad@example.com"
                />
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.location.href = '/'}
              disabled={isSubmitting}
            >
              {t('orgSettings.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!hasChanges || isSubmitting}
            >
              {isSubmitting ? t('orgSettings.saving') : `💾 ${t('orgSettings.saveChanges')}`}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
